/**
 * `SettingsOrigins` — per-site flagged-origin review + allow-list
 * management (ADR-0057 §5). Destination for the SiteCard's
 * "UNREGISTERED ORIGINS · Review →" warning band; also reachable
 * directly from the Settings sidebar.
 *
 * Two stacked panels:
 *
 *   1. Unregistered origins — distinct flagged hosts the merchant has
 *      not (yet) registered for this site, fetched via
 *      `getUnregisteredOrigins(site_id)`. Sorted by count DESC server-
 *      side; the dashboard preserves order. Each row carries a
 *      mismatch-reason pill, the count, first-/last-seen relative
 *      timestamps, and an "Add to allow-list" action that calls
 *      `updateAllowedOrigins(site_id, 'add', host)`. The `no_referrer`
 *      bucket has `observed_host === null`; we render it as a
 *      separate-row marker (no Add action — there's no host to add).
 *
 *   2. Allow-list — current per-site allow-list, fetched via
 *      `getAllowedOrigins(site_id)`. Each row has a Remove action that
 *      calls `updateAllowedOrigins(site_id, 'remove', host)`. Apex +
 *      `www.` auto-match the registered domain; we render those as a
 *      static "auto-matched" hint so the merchant doesn't have to
 *      add them.
 *
 * Loading / error states surface honestly — a fetch failure shows a
 * Retry button rather than silently blanking the panel. Mutations
 * refresh both lists so a newly allow-listed host vanishes from the
 * flagged panel on next aggregate recompute and immediately appears
 * in the allow-list panel.
 *
 * Design tokens come from `useC()` — amber for the warning context
 * (matches the SiteCard band), lime for "allow-listed", thin 1px
 * borders, small radii, mono uppercase kickers. No hard-coded hex.
 */

import { AlertTriangle, Check, Plus, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { Site } from '@sill/types';
import type {
  AllowedOriginsResult,
  UnregisteredOriginsResult,
} from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { Card, Pill, RelativeTime } from '../../components/atoms/index.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export interface SettingsOriginsProps {
  site: Site;
}

type ThemeC = ReturnType<typeof useC>;
type UnregisteredItem = UnregisteredOriginsResult['items'][number];

type LoadState<T> =
  | { kind: 'loading' }
  | { kind: 'ready'; value: T }
  | { kind: 'error'; message: string };

export function SettingsOrigins({ site }: SettingsOriginsProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const toast = useToast();

  const [unregistered, setUnregistered] = useState<
    LoadState<UnregisteredOriginsResult>
  >({ kind: 'loading' });
  const [allowed, setAllowed] = useState<LoadState<AllowedOriginsResult>>({
    kind: 'loading',
  });
  // Per-host pending action (the one host whose Add/Remove is in flight)
  // so we can disable the row's button + spin the icon. A single key is
  // enough because the merchant clicks one row at a time.
  const [pendingHost, setPendingHost] = useState<string | null>(null);

  const refreshUnregistered = useCallback(async (): Promise<void> => {
    setUnregistered({ kind: 'loading' });
    try {
      const res = await api.sites.getUnregisteredOrigins(site.site_id);
      setUnregistered({ kind: 'ready', value: res });
    } catch (err) {
      setUnregistered({
        kind: 'error',
        message: explainError(err),
      });
    }
  }, [api, site.site_id]);

  const refreshAllowed = useCallback(async (): Promise<void> => {
    setAllowed({ kind: 'loading' });
    try {
      const res = await api.sites.getAllowedOrigins(site.site_id);
      setAllowed({ kind: 'ready', value: res });
    } catch (err) {
      setAllowed({
        kind: 'error',
        message: explainError(err),
      });
    }
  }, [api, site.site_id]);

  useEffect(() => {
    void refreshUnregistered();
    void refreshAllowed();
  }, [refreshUnregistered, refreshAllowed]);

  const handleAdd = useCallback(
    async (host: string): Promise<void> => {
      if (pendingHost !== null) return;
      setPendingHost(host);
      try {
        const res = await api.sites.updateAllowedOrigins(
          site.site_id,
          'add',
          host,
        );
        // Optimistically reflect the new allow-list; the unregistered
        // bucket may still include the host until the server's
        // aggregate recomputes, but a refetch is the honest answer.
        setAllowed({ kind: 'ready', value: res });
        toast({
          title: 'Added to allow-list',
          body: `${host} will no longer be flagged on ${site.domain}.`,
          tone: 'lime',
        });
        await refreshUnregistered();
      } catch (err) {
        toast({
          title: 'Could not add to allow-list',
          body: explainError(err),
          tone: 'coral',
        });
      } finally {
        setPendingHost(null);
      }
    },
    [api, pendingHost, refreshUnregistered, site.domain, site.site_id, toast],
  );

  const handleRemove = useCallback(
    async (host: string): Promise<void> => {
      if (pendingHost !== null) return;
      setPendingHost(host);
      try {
        const res = await api.sites.updateAllowedOrigins(
          site.site_id,
          'remove',
          host,
        );
        setAllowed({ kind: 'ready', value: res });
        toast({
          title: 'Removed from allow-list',
          body: `${host} will be flagged again if it appears in traffic.`,
          tone: 'amber',
        });
        await refreshUnregistered();
      } catch (err) {
        toast({
          title: 'Could not remove from allow-list',
          body: explainError(err),
          tone: 'coral',
        });
      } finally {
        setPendingHost(null);
      }
    },
    [api, pendingHost, refreshUnregistered, site.domain, site.site_id, toast],
  );

  return (
    <div className="space-y-5">
      <Card className="p-5 sm:p-6">
        <Header
          C={C}
          title="Origins"
          body={
            <>
              These are origins where your embed fired that don&apos;t
              match this site&apos;s registered domain. The apex (
              <span style={{ fontFamily: monoFont }}>{site.domain}</span>)
              and its <span style={{ fontFamily: monoFont }}>www.</span>{' '}
              subdomain auto-match — add subdomains or extra domains here.
            </>
          }
        />

        <SectionHeading
          C={C}
          label="Unregistered origins"
          hint="Sorted by record count, last 30 days."
        />
        {unregistered.kind === 'loading' ? (
          <LoadingRow C={C} />
        ) : unregistered.kind === 'error' ? (
          <ErrorRow
            C={C}
            message={unregistered.message}
            onRetry={() => void refreshUnregistered()}
          />
        ) : unregistered.value.total_records === 0 ? (
          <EmptyRow
            C={C}
            label="No unregistered origins"
            body="All recorded traffic is from this site's registered domain."
          />
        ) : (
          <UnregisteredList
            C={C}
            items={unregistered.value.items}
            pendingHost={pendingHost}
            existingAllowed={
              allowed.kind === 'ready' ? allowed.value.allowed_origins : []
            }
            onAdd={(host) => void handleAdd(host)}
          />
        )}
      </Card>

      <Card className="p-5 sm:p-6">
        <SectionHeading
          C={C}
          label="Allow-list"
          hint={`Hosts that won't be flagged on ${site.domain}.`}
        />
        {allowed.kind === 'loading' ? (
          <LoadingRow C={C} />
        ) : allowed.kind === 'error' ? (
          <ErrorRow
            C={C}
            message={allowed.message}
            onRetry={() => void refreshAllowed()}
          />
        ) : allowed.value.allowed_origins.length === 0 ? (
          <EmptyRow
            C={C}
            label="No additional allow-listed origins"
            body={`Only ${site.domain} and www.${site.domain} are auto-matched. Add a flagged host above to allow-list it.`}
          />
        ) : (
          <AllowedList
            C={C}
            hosts={allowed.value.allowed_origins}
            pendingHost={pendingHost}
            onRemove={(host) => void handleRemove(host)}
          />
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Header({
  C,
  title,
  body,
}: {
  C: ThemeC;
  title: string;
  body: React.ReactNode;
}): JSX.Element {
  return (
    <div className="mb-5 pb-4" style={{ borderBottom: `1px solid ${C.border}` }}>
      <div
        style={{
          fontFamily: serifFont,
          fontSize: 22,
          color: C.text,
          letterSpacing: -0.5,
          marginBottom: 4,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        {body}
      </div>
    </div>
  );
}

function SectionHeading({
  C,
  label,
  hint,
}: {
  C: ThemeC;
  label: string;
  hint?: string;
}): JSX.Element {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3 flex-wrap">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          fontWeight: 600,
        }}
      >
        {label.toUpperCase()}
      </div>
      {hint !== undefined ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
          }}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function UnregisteredList({
  C,
  items,
  pendingHost,
  existingAllowed,
  onAdd,
}: {
  C: ThemeC;
  items: UnregisteredOriginsResult['items'];
  pendingHost: string | null;
  existingAllowed: ReadonlyArray<string>;
  onAdd: (host: string) => void;
}): JSX.Element {
  const allowedSet = new Set(existingAllowed);
  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <UnregisteredRow
          key={`${item.observed_host ?? 'no_referrer'}_${idx}`}
          C={C}
          item={item}
          pending={pendingHost === item.observed_host}
          alreadyAllowed={
            item.observed_host !== null && allowedSet.has(item.observed_host)
          }
          onAdd={onAdd}
        />
      ))}
    </div>
  );
}

function UnregisteredRow({
  C,
  item,
  pending,
  alreadyAllowed,
  onAdd,
}: {
  C: ThemeC;
  item: UnregisteredItem;
  pending: boolean;
  alreadyAllowed: boolean;
  onAdd: (host: string) => void;
}): JSX.Element {
  const hasHost = item.observed_host !== null;
  const reasonTone = item.mismatch_reason === 'no_referrer' ? 'neutral' : 'amber';
  const reasonLabel =
    item.mismatch_reason === 'no_referrer'
      ? 'NO REFERRER'
      : 'HOST MISMATCH';

  return (
    <div
      className="p-3 flex items-center gap-3 flex-wrap"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 13,
              color: C.text,
              wordBreak: 'break-all',
            }}
          >
            {hasHost ? item.observed_host : '(no referrer)'}
          </span>
          <Pill tone={reasonTone}>{reasonLabel}</Pill>
          {alreadyAllowed ? (
            <Pill tone="lime" icon={Check}>
              ALLOW-LISTED
            </Pill>
          ) : null}
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
          }}
        >
          {item.count.toLocaleString()}{' '}
          {item.count === 1 ? 'record' : 'records'} · first{' '}
          <RelativeTime iso={item.first_seen_at} /> · last{' '}
          <RelativeTime iso={item.last_seen_at} />
        </div>
      </div>
      {hasHost && !alreadyAllowed ? (
        <button
          type="button"
          onClick={() => onAdd(item.observed_host as string)}
          disabled={pending}
          className="flex items-center gap-2 px-3 py-2"
          style={{
            background: pending ? C.surface3 : C.lime,
            color: pending ? C.muted : C.ink,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 12,
            borderRadius: 2,
            opacity: pending ? 0.7 : 1,
            cursor: pending ? 'wait' : 'pointer',
          }}
        >
          {pending ? (
            <>
              <RefreshCw
                size={12}
                style={{ animation: 'spin 1.4s linear infinite' }}
              />
              Adding…
            </>
          ) : (
            <>
              <Plus size={12} />
              Add to allow-list
            </>
          )}
        </button>
      ) : null}
    </div>
  );
}

function AllowedList({
  C,
  hosts,
  pendingHost,
  onRemove,
}: {
  C: ThemeC;
  hosts: ReadonlyArray<string>;
  pendingHost: string | null;
  onRemove: (host: string) => void;
}): JSX.Element {
  return (
    <div className="space-y-2">
      {hosts.map((host) => (
        <div
          key={host}
          className="p-3 flex items-center gap-3 flex-wrap"
          style={{
            background: C.surface2,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
          }}
        >
          <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 13,
                color: C.text,
                wordBreak: 'break-all',
              }}
            >
              {host}
            </span>
            <Pill tone="lime" icon={Check}>
              ALLOW-LISTED
            </Pill>
          </div>
          <button
            type="button"
            onClick={() => onRemove(host)}
            disabled={pendingHost === host}
            className="flex items-center gap-2 px-3 py-2"
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.coralFg,
              fontFamily: sansFont,
              fontSize: 12,
              fontWeight: 500,
              borderRadius: 2,
              opacity: pendingHost === host ? 0.7 : 1,
              cursor: pendingHost === host ? 'wait' : 'pointer',
            }}
          >
            {pendingHost === host ? (
              <>
                <RefreshCw
                  size={12}
                  style={{ animation: 'spin 1.4s linear infinite' }}
                />
                Removing…
              </>
            ) : (
              <>
                <X size={12} />
                Remove
              </>
            )}
          </button>
        </div>
      ))}
    </div>
  );
}

function LoadingRow({ C }: { C: ThemeC }): JSX.Element {
  return (
    <div
      className="p-3 flex items-center gap-2"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <RefreshCw
        size={12}
        color={C.muted}
        style={{ animation: 'spin 1.4s linear infinite' }}
      />
      <span
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
        }}
      >
        Loading…
      </span>
    </div>
  );
}

function ErrorRow({
  C,
  message,
  onRetry,
}: {
  C: ThemeC;
  message: string;
  onRetry: () => void;
}): JSX.Element {
  return (
    <div
      className="p-3 flex items-center gap-3 flex-wrap"
      style={{
        background: C.coralDim,
        border: `1px solid rgba(255,107,71,0.3)`,
        borderRadius: 2,
      }}
    >
      <AlertTriangle size={14} color={C.coralFg} />
      <div className="flex-1 min-w-0">
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.text,
            lineHeight: 1.5,
          }}
        >
          {message}
        </div>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="flex items-center gap-2 px-3 py-2"
        style={{
          background: 'transparent',
          border: `1px solid ${C.border}`,
          color: C.text,
          fontFamily: sansFont,
          fontSize: 12,
          fontWeight: 500,
          borderRadius: 2,
        }}
      >
        <RefreshCw size={12} />
        Retry
      </button>
    </div>
  );
}

function EmptyRow({
  C,
  label,
  body,
}: {
  C: ThemeC;
  label: string;
  body: string;
}): JSX.Element {
  return (
    <div
      className="p-4"
      style={{
        background: C.surface2,
        border: `1px dashed ${C.border}`,
        borderRadius: 2,
      }}
    >
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          fontWeight: 500,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        {body}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function explainError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.kind) {
      case 'forbidden':
        return 'Your role cannot manage this allow-list.';
      case 'not_found':
        return 'This site is no longer accessible.';
      case 'bad_request':
        return 'That host is not a valid origin.';
      case 'rate_limited':
        return 'Too many requests. Try again in a moment.';
      case 'service_unavailable':
        return 'This surface is temporarily unavailable.';
      default:
        return err.message.length > 0 ? err.message : 'Unknown error.';
    }
  }
  if (err instanceof Error && err.message.length > 0) {
    return err.message;
  }
  return 'Unknown error.';
}
